import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Package,
  AlertCircle
} from 'lucide-react';
import { getAuthHeaders } from '../../utils/getauthheaders';
import { useAuth } from '../../hooks/useAuth';
import { Header } from './components/Header';
import { MenuOverlay } from './components/MenuOverlay';
import { StatsGrid } from './components/StatsGrid';
import { CourseCard } from './components/CourseCard';
import { CourseDetailsModal } from './components/CourseDetailsModal';
import { CMRUploadModal } from './components/CMRUploadModa';
import { CashModal } from './components/CashModal';
import { getNextDBStatus } from './utilities/utilities';

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

// Types
type DBStatus = 'in_asteptare' | 'in_incarcare' | 'pe_drum' | 'livrat';

interface Course {
  id: string;
  from: string;
  to: string;
  client: string;
  phone: string;
  date: string;
  time: string;
  paymentType: string;
  amount: number;
  status: DBStatus;
  cmrStartUploaded: boolean;
  cmrEndUploaded: boolean;
  vehicleNumber?: string;
  vehicleName?: string;
  details: {
    weight?: string;
    goods: string;
    distance: string;
    notes?: string;
    priority: string;
    progress: number;
  };
}

// Main Component
function DriverDashboard() {
  const { user: currentUser, isLoggedIn } = useAuth();

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCMRUpload, setShowCMRUpload] = useState(false);
  const [currentCMRType, setCurrentCMRType] = useState<'start' | 'end'>('start');
  const [cmrImage, setCmrImage] = useState<{
    file: File;
    preview: string;
    name: string;
  } | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [vehicleData, setVehicleData] = useState<any>(null);
  const [uploadingCMR, setUploadingCMR] = useState(false);
  const [driverStats, setDriverStats] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cashCollections, setCashCollections] = useState<any[]>([]);
  const [showCashModal, setShowCashModal] = useState(false);
  const [reportingCash, setReportingCash] = useState(false);
  const [cashTotals, setCashTotals] = useState({ pending: 0, collected: 0 });

  const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
    const defaultOptions: RequestInit = {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      ...options,
    };

    if (options.body instanceof FormData) {
      delete (defaultOptions.headers as any)['Content-Type'];
    }

    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  };

  const loadCashCollections = useCallback(async (retryCount = 0) => {
    if (!currentUser?.id) return;

    try {
      const data = await makeAuthenticatedRequest(
        `${API_BASE_URL}/api/cash/driver/${currentUser.id}`
      );
      
      if (data.success) {
        setCashCollections(data.data.collections || []);
        setCashTotals(data.data.totals || { pending: 0, collected: 0 });
      } else {
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (err: any) {
      console.error('Error loading cash collections:', err);
      
      if (retryCount < 2 && (err.message.includes('fetch') || err.message.includes('network'))) {
        setTimeout(() => {
          loadCashCollections(retryCount + 1);
        }, 1000 * (retryCount + 1));
        return;
      }
      
      setCashCollections([]);
      setCashTotals({ pending: 0, collected: 0 });
      
      if (retryCount === 0) {
        setError('Eroare la încărcarea informațiilor de cash: ' + err.message);
      }
    }
  }, [currentUser?.id]);

  const reportCashCollection = async (collectionId: number, notes?: string) => {
    try {
      setReportingCash(true);
      
      const response = await fetch(`${API_BASE_URL}/api/cash/report/${collectionId}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: notes || '' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        await loadCashCollections();
        setShowCashModal(false);
        alert(`Cash-ul de ${result.data.amount} RON pentru ${result.data.client} a fost raportat cu succes!`);
      } else {
        throw new Error(result.message || 'Răspuns invalid de la server');
      }

    } catch (err: any) {
      console.error('Error reporting cash:', err);
      alert('Eroare la raportarea cash-ului: ' + (err.message || 'Eroare necunoscută'));
    } finally {
      setReportingCash(false);
    }
  };

  const loadShipments = useCallback(async () => {
    if (!currentUser?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await makeAuthenticatedRequest(
        `${API_BASE_URL}/api/drivers/${currentUser.id}/shipments`
      );
      
      if (data.success && Array.isArray(data.data)) {
        const formattedCourses: Course[] = data.data.map((shipment: any) => {
          const transport = shipment.transport || {};
          const client = shipment.client || {};
          const ruta = shipment.ruta || {};
          const vehicul_sofer = shipment.vehicul_sofer || {};
          const descriere = shipment.descriere || {};
          const raw_data = shipment.raw_data || {};
          const locatii = shipment.locatii || {};
          const contacte = shipment.contacte || {};
          const transport_group = shipment.transport_group || {};
          const cmr = shipment.cmr || {};
          
          const formatClientName = () => {
            const clientName = client.nume_firma || 'Client necunoscut';
            const destinatarName = contacte.nume_destinatar;
            
            if (destinatarName && destinatarName.trim()) {
              return `${clientName} → ${destinatarName}`;
            }
            return clientName;
          };

          return {
            id: transport.id || raw_data.id || 'unknown',
            tracking_token: raw_data.tracking_token || '',
            from: locatii.plecare?.adresa_completa || ruta.plecare || 'Plecare necunoscută',
            to: locatii.destinatie?.adresa_completa || ruta.destinatie || 'Destinație necunoscută',
            client: formatClientName() || 'Client necunoscut',
            clientId: client.id || raw_data.client_id,
            expeditorPhone: contacte.telefon_expeditor || '',
            destinatarPhone: contacte.telefon_destinatar || '',
            destinatarName: contacte.nume_destinatar || '',
            date: transport.data_transport || new Date().toLocaleDateString('ro-RO'),
            time: raw_data.ora_plecarii ? 
              new Date(raw_data.ora_plecarii).toLocaleTimeString('ro-RO', {
                hour: '2-digit',
                minute: '2-digit'
              }) : '00:00',
            estimatedTime: raw_data.ora_estimata ? 
              new Date(raw_data.ora_estimata).toLocaleString('ro-RO') : null,
            paymentType: transport.tip_plata || raw_data.tip_plata || 'cash',
            amount: parseFloat(raw_data.value || 0),
            formattedAmount: transport.valoare || `${parseFloat(raw_data.value || 0)} RON`,
            status: raw_data.status || 'in_asteptare',
            progress: parseInt(raw_data.progres || 0),
            priority: raw_data.prioritate === 'urgenta' ? 'Urgentă' : 'Normală',
            vehicleNumber: vehicul_sofer.numar_vehicul || 'N/A',
            vehicleName: vehicul_sofer.vehicul || 'Vehicul necunoscut',
            vehicleComplete: vehicul_sofer.vehicul_complet || vehicul_sofer.vehicul || 'Vehicul necunoscut',
            vehicleBrandModel: vehicul_sofer.vehicul_marca_model || '',
            driverPhone: vehicul_sofer.sofer_telefon || '',
            vehiclePosition: vehicul_sofer.pozitie_vehicul || null,
            vehicleAddress: vehicul_sofer.vehicul_adresa || null,
            vehicleSpeed: vehicul_sofer.vehicul_viteza || null,
            vehicleLastUpdate: vehicul_sofer.vehicul_ultima_pozitie || null,
            departureCoordinates: ruta.plecare_coordonate || locatii.plecare?.coordonate || null,
            destinationCoordinates: ruta.destinatie_coordonate || locatii.destinatie?.coordonate || null,
            cmrStartUploaded: cmr.cmr_pornire_uploaded || false,
            cmrEndUploaded: cmr.cmr_finalizare_uploaded || false,
            parentTransportId: transport_group.parent_transport_id || raw_data.parent_transport_id,
            transportGroupId: transport_group.transport_group_id || raw_data.transport_group_id,
            sequenceNumber: transport_group.sequence_number || raw_data.sequence_number || 1,
            isMainTransport: transport_group.is_main_transport !== undefined ? 
              transport_group.is_main_transport : (raw_data.is_main_transport !== 0),
            details: {
              weight: descriere.tip_marfa || 'N/A',
              goods: descriere.detalii_marfa || 'Mărfuri diverse',
              route: ruta.ruta_completa || `${ruta.plecare || ''} → ${ruta.destinatie || ''}`,
              notes: descriere.descriere_completa || '',
              priority: raw_data.prioritate === 'urgenta' ? 'Urgentă' : 'Normală',
              progress: parseInt(raw_data.progres || 0),
              departureLocation: {
                address: locatii.plecare?.adresa || '',
                city: locatii.plecare?.oras || '',
                county: locatii.plecare?.judet || '',
                country: locatii.plecare?.tara || 'Romania',
                coordinates: locatii.plecare?.coordonate || null
              },
              destinationLocation: {
                address: locatii.destinatie?.adresa || '',
                city: locatii.destinatie?.oras || '',
                county: locatii.destinatie?.judet || '',
                country: locatii.destinatie?.tara || 'Romania',
                coordinates: locatii.destinatie?.coordonate || null
              },
              vehicleKm: vehicul_sofer.km_vehicul || 0,
              fuel: vehicul_sofer.combustibil || '',
              createdAt: raw_data.created_at,
              updatedAt: raw_data.updated_at
            }
          };
        });
        
        setCourses(formattedCourses);
      } else {
        setCourses([]);
      }
    } catch (err: any) {
      console.error('Error loading shipments:', err);
      setError('Eroare la încărcarea curselor: ' + (err.message || 'Eroare necunoscută'));
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  const loadDriverStats = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      const data = await makeAuthenticatedRequest(
        `${API_BASE_URL}/api/drivers/${currentUser.id}/stats`
      );
      if (data.success) {
        setDriverStats(data.data);
      }
    } catch (err) {
      console.error('Error loading driver stats:', err);
    }
  }, [currentUser?.id]);

  const loadVehicleData = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      const data = await makeAuthenticatedRequest(
        `${API_BASE_URL}/api/available-vehicles?employeeId=${currentUser.id}`
      );
      if (data.success && Array.isArray(data.data) && data.data.length > 0) {
        const assignedVehicle = data.data.find((v: any) => v.assigned_employee_id == currentUser.id) || data.data[0];
        setVehicleData(assignedVehicle);
      }
    } catch (err) {
      console.error('Error loading vehicle data:', err);
    }
  }, [currentUser?.id]);

  const updateCourseStatus = async (courseId: string, newStatus: DBStatus) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/driver/shipments/${courseId}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status: newStatus
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update status');
    }

    const result = await response.json();

    // Update local state
    setCourses(prev => prev.map(course =>
      course.id === courseId ? {
        ...course,
        status: newStatus,
        details: {
          ...course.details,
          progress: result.data?.progres || course.details.progress
        }
      } : course
    ));

  } catch (err: any) {
    console.error('Error updating status:', err);
    alert('Eroare la actualizarea statusului: ' + (err.message || 'Eroare necunoscută'));
  }
};

  const handleCMRUpload = (courseId: string, type: 'start' | 'end') => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setSelectedCourse(course);
      setCurrentCMRType(type);
      setShowCMRUpload(true);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCmrImage({
          file: file,
          preview: e.target?.result as string,
          name: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadCMR = async () => {
  if (!cmrImage || !selectedCourse) return;

  try {
    setUploadingCMR(true);
    const formData = new FormData();
    formData.append('image', cmrImage.file);
    formData.append('shipment_id', selectedCourse.id);
    formData.append('document_type', currentCMRType === 'start' ? 'cmr_pornire' : 'cmr_finalizare');

    const response = await fetch(`${API_BASE_URL}/api/cmr/upload`, {
      method: 'POST',
      headers: {
        ...(localStorage.getItem('token') && {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        })
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Upload failed');
    }

    const result = await response.json();
    
    if (result.success) {
      // Update local state immediately
      if (currentCMRType === 'start') {
        setCourses(prev => prev.map(course =>
          course.id === selectedCourse.id ? {
            ...course,
            cmrStartUploaded: true,
            status: 'pe_drum' as DBStatus,
            details: {
              ...course.details,
              progress: 50
            }
          } : course
        ));
      } else {
        setCourses(prev => prev.map(course =>
          course.id === selectedCourse.id ? {
            ...course,
            cmrEndUploaded: true,
            status: 'livrat' as DBStatus,
            details: {
              ...course.details,
              progress: 100
            }
          } : course
        ));
      }

      setShowCMRUpload(false);
      setCmrImage(null);
      setSelectedCourse(null);
      
      alert(currentCMRType === 'start' 
        ? 'CMR Pornire încărcat cu succes! Status schimbat în "Pe drum".' 
        : 'CMR Finalizare încărcat cu succes! Cursa a fost finalizată.'
      );

      // Reload to get fresh data from server
      await loadShipments();
      
      // If delivery is complete, reload cash collections
      if (currentCMRType === 'end') {
        setTimeout(() => {
          loadCashCollections();
        }, 1000);
      }
    }
  } catch (err: any) {
    console.error('Error uploading CMR:', err);
    alert('Eroare la încărcarea CMR: ' + (err.message || 'Eroare necunoscută'));
  } finally {
    setUploadingCMR(false);
  }
};

  const handleMenuAction = (action: string) => {
    setShowMenu(false);
    
    switch(action) {
      case 'cash':
        setShowCashModal(true);
        break;
      case 'logout':
        if (window.confirm('Sigur vrei să te deloghezi?')) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isLoggedIn && currentUser) {
      loadShipments();
      loadDriverStats();
      loadVehicleData();
      loadCashCollections();
    }
  }, [isLoggedIn, currentUser, loadShipments, loadDriverStats, loadVehicleData, loadCashCollections]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-white">Se încarcă cursele...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900">
      <Header 
        currentUser={currentUser}
        onMenuClick={() => setShowMenu(true)}
        cashTotals={cashTotals}
        onCashClick={() => setShowCashModal(true)}
      />

      <MenuOverlay
        show={showMenu}
        onClose={() => setShowMenu(false)}
        currentUser={currentUser}
        cashTotals={cashTotals}
        cashCollectionsCount={cashCollections.filter(c => c.status === 'pending').length}
        onMenuAction={handleMenuAction}
      />

      <div className="p-4 pb-20">
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-4 mb-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-300">{error}</span>
            </div>
          </div>
        )}

        <StatsGrid courses={courses} />

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-bold text-lg">Cursele tale</h2>
            <button 
              onClick={loadShipments}
              className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 rounded-lg py-1 px-3 text-sm font-medium"
            >
              Reîmprospătează
            </button>
          </div>
          
          {courses.length === 0 ? (
            <div className="bg-white/10 rounded-2xl p-6 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-300 mb-2">Nu ai curse atribuite momentan</p>
              <button 
                onClick={loadShipments}
                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 rounded-xl py-2 px-4 text-sm font-medium"
              >
                Reîncarcă curse
              </button>
            </div>
          ) : (
            courses.map(course => (
              <CourseCard 
                key={course.id}
                course={course}
                onShowDetails={(course) => {
                  setSelectedCourse(course);
                  setShowDetails(true);
                }}
                onCMRUpload={handleCMRUpload}
                onUpdateStatus={updateCourseStatus}
              />
            ))
          )}
        </div>
      </div>

      <CourseDetailsModal
        show={showDetails}
        course={selectedCourse}
        onClose={() => {
          setShowDetails(false);
          setSelectedCourse(null);
        }}
        onCMRUpload={handleCMRUpload}
        onUpdateStatus={updateCourseStatus}
      />

      <CMRUploadModal
        show={showCMRUpload}
        course={selectedCourse}
        cmrType={currentCMRType}
        cmrImage={cmrImage}
        uploading={uploadingCMR}
        onClose={() => {
          setShowCMRUpload(false);
          setCmrImage(null);
          setSelectedCourse(null);
        }}
        onFileSelect={handleFileSelect}
        onUpload={uploadCMR}
        onRemoveImage={() => {
          setCmrImage(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        fileInputRef={fileInputRef}
      />

      <CashModal
        show={showCashModal}
        cashCollections={cashCollections}
        cashTotals={cashTotals}
        reporting={reportingCash}
        onClose={() => setShowCashModal(false)}
        onRefresh={loadCashCollections}
        onReport={reportCashCollection}
      />
    </div>
  );
}

export default DriverDashboard;